import { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigationType } from "react-router-dom";
import { useAuthStore } from "@/domain/authStore";
import { apiClient } from "@/domain/api";
import { shouldSealHistoryEntry } from "@/domain/authSessionGuard";
import type { AuthUser } from "@/domain/authStore";

/**
 * Wraps protected routes. On first mount checks whether a jwt_authorisation
 * cookie exists (page refresh scenario) by calling GET /auth/me. While the
 * check is in flight the tree is suspended. If unauthenticated, redirects to
 * /login. Also listens for il:unauthorized events (dispatched by the axios
 * interceptor on 401) so expired tokens are caught mid-session.
 */
export function AuthGuard() {
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const navigationType = useNavigationType();

  // null = still checking, false = checked (no session)
  const [checking, setChecking] = useState<boolean>(!user);

  // Computed synchronously during render (not an effect) so a sealed entry
  // redirects to /login before its protected content ever paints, matching
  // how the plain `!user` check below already works - see authSessionGuard.ts.
  const sealed = useMemo(() => {
    if (!user) return false;
    return shouldSealHistoryEntry({
      userId: String(user.id),
      locationKey: location.key,
      isPop: navigationType === "POP",
    });
  }, [user, location.key, navigationType]);

  useEffect(() => {
    if (user) {
      setChecking(false);
      return;
    }
    apiClient
      .get<AuthUser & { token: string }>("/auth/me")
      .then(({ data }) => {
        login(data, data.token);
      })
      .catch(() => {
        // no valid session — AuthGuard will redirect to /login below
      })
      .finally(() => setChecking(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener("il:unauthorized", onUnauthorized);
    return () => window.removeEventListener("il:unauthorized", onUnauthorized);
  }, [logout]);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="w-5 h-5 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user || sealed) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
